import React from 'react';
import Highcharts, { Chart as ChartRef } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Exporting from 'highcharts/modules/exporting';
import { debounce } from 'lodash-es';

import { StoreProps } from 'store';
import Utils from 'helpers/Utils';

import { reflowAllCharts } from 'actions/analytics';
import { ChartViewData } from 'types/Analytics';
import { LayoutTypes } from 'components/modal/modalList/editLayout/EditLayout';

Exporting(Highcharts);

interface Props extends StoreProps {
  data: ChartViewData;
  setRef?: (ref: ChartRef) => void;
  className?: string;
  shouldResize: boolean;
  layoutType?: LayoutTypes;
  isMenuExpanded: boolean;
  styleOptions?: Highcharts.Options;
  transformOptions?: (
    options: Highcharts.Options,
    chartType?: string
  ) => Highcharts.Options;
}

interface State {
  width: number;
  uuid: string;
}

const TIMEOUT = 500;
const GRAPH_CARD_PADDING = 48;

class ChartView extends React.Component<Props, State> {
  private chartRef: ChartRef | undefined;
  private chartContainerRef: HTMLDivElement | null | undefined;

  constructor(props) {
    super(props);

    this.setRef = this.setRef.bind(this);
    this.state = {
      width: 0,
      uuid: 'uuid' in this.props.data ? this.props.data.uuid : '',
    };
  }

  componentDidMount() {
    const { shouldResize } = this.props;

    if (shouldResize) {
      window.addEventListener('resize', this.debouncedFitWidth());
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { shouldResize, layoutType, data, isMenuExpanded } = this.props;

    if (shouldResize) {
      const menuChanged = prevProps.isMenuExpanded !== isMenuExpanded;
      const layoutChanged = prevProps.layoutType !== layoutType;
      const refreshIdChanged = 'uuid' in data && data.uuid !== prevState.uuid;

      if (menuChanged || layoutChanged || refreshIdChanged) {
        this.fitWidth();
      }
    }
  }

  componentWillUnmount() {
    const { shouldResize } = this.props;

    if (shouldResize) {
      window.removeEventListener('resize', this.debouncedFitWidth());
    }
  }

  /**
   * Page after canceling print doesn't resize chart
   * https://github.com/highcharts/highcharts/issues/1093
   *
   * mount -> current uuid to state -> print -> reflowAllCharts ->
   * -> new uuid -> compare with uuid in state -> fitWidth()
   */
  get chartEvents() {
    const { dispatch } = this.props;

    return {
      afterPrint: () => {
        dispatch(reflowAllCharts());
      },
    };
  }

  render() {
    const { data, className, styleOptions, transformOptions } = this.props;
    const { width } = this.state;
    const chart = 'chart' in data ? data.chart : undefined;
    const chartData = typeof chart === 'object' ? chart : {};

    let options = Highcharts.merge(data as Highcharts.Options, styleOptions, {
      chart: {
        ...chartData,
        width: width === 0 ? undefined : width,
        events: this.chartEvents,
      },
    });

    if (transformOptions) {
      const chartType = 'chartType' in data ? data.chartType : undefined;
      options = transformOptions(options, chartType as string | undefined);
    }

    return (
      <div
        className={className}
        ref={(ref) => {
          this.chartContainerRef = ref;
        }}>
        <HighchartsReact
          ref={this.setRef}
          highcharts={Highcharts}
          options={options}
        />
      </div>
    );
  }

  setRef(ref) {
    const { setRef } = this.props;

    if (!ref) {
      return;
    }

    this.chartRef = ref.chart;
    setRef && setRef(ref);
  }

  /**
   * Highcharts по умолчанию не умеет ресайзить график
   * под ширину контейнера. Поэтому при наличии резиновых
   * контейнерах это необходимо делать руками. Метод обходит
   * условие полноэкранного режима, где ресайзить ничего не
   * нужно. Ресайзинг происходит по ширине карточки графика
   * минус отступы с двух сторон.
   *
   * При инитном рендере ширину передавать не нужно, она
   * выставляется по ширине flex-контейнера
   */

  debouncedFitWidth = () => debounce(this.fitWidth, TIMEOUT);

  fitWidth = () => {
    if (this.chartContainerRef) {
      if (!this.chartRef) {
        return;
      }
      let isFullScreen: boolean | undefined = false;
      if (
        this.chartRef &&
        Utils.hasProp(this.chartRef.fullscreen, 'unbindFullscreenEvent')
      ) {
        isFullScreen = this.chartRef.fullscreen.isOpen;
      }
      if (isFullScreen) {
        return;
      }

      const chartContainerParentEl = this.chartContainerRef.parentElement;
      const graphCardEl =
        chartContainerParentEl !== null
          ? chartContainerParentEl.parentElement
          : null;

      if (graphCardEl !== null) {
        this.setState({
          width: graphCardEl.getBoundingClientRect().width - GRAPH_CARD_PADDING,
        });
      }
    }
  };
}

export default ChartView;
